import React, { useCallback, useRef, useState } from "react";
import { GrAttachment } from "react-icons/gr";
import { MdError, MdCloudUpload } from "react-icons/md";
import { HiDocumentText } from "react-icons/hi";
import { _formatFileSize } from "../../../../utils/common";
import toast from "react-hot-toast";

const BulkFileUploader = ({ onFileUpload, acceptedFormats = [".csv", ".xlsx", ".xls"] }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    const extension = "." + file.name.split(".").pop().toLowerCase();
    if (!acceptedFormats.includes(extension)) {
      toast.error(
        `Invalid file format. Please upload ${acceptedFormats.join(", ")} files.`
      );
      return false;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error("File size exceeds 10MB limit.");
      return false;
    }

    return true;
  };

  const handleFileSelect = useCallback(
    async (file) => {
      if (!validateFile(file)) return;

      setUploadedFile(file);
      setIsProcessing(true);

      // Simulate file processing
      setTimeout(() => {
        setIsProcessing(false);
        onFileUpload(file);
      }, 1000);
    },
    [onFileUpload, acceptedFormats]
  );

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-4" role="region" aria-label="File upload">
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
        accept={acceptedFormats.join(",")}
        aria-label="Select file to upload"
      />

      {!uploadedFile ? (
        <div
          className={`
            border-2 border-dashed rounded-lg p-12 text-center transition-all
            ${
              isDragging
                ? "border-theme bg-information-50"
                : "border-borderColor bg-primary-50 hover:border-theme hover:bg-information-50"
            }
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleButtonClick();
            }
          }}
          aria-label="Drag and drop file here or click to browse"
        >
          <div className="flex flex-col items-center gap-4">
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] w-16 h-16 rounded-full flex items-center justify-center text-white">
              <MdCloudUpload className="text-3xl" />
            </div>
            <div>
              <p className="text-theme font-medium text-lg mb-1">
                Drag and drop your file here
              </p>
              <p className="text-textLight text-sm">
                or{" "}
                <button
                  type="button"
                  onClick={handleButtonClick}
                  className="text-theme underline hover:text-themeHover"
                >
                  browse files
                </button>
              </p>
            </div>
            <div className="mt-2">
              <p className="text-xs text-textLight">
                Accepted formats: {acceptedFormats.join(", ")} (Max 10MB)
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-borderColor rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <div className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] w-12 h-12 rounded-md flex items-center justify-center text-white">
                <HiDocumentText className="text-2xl" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-theme font-medium text-sm truncate">
                  {uploadedFile.name}
                </p>
                <p className="text-textLight text-xs">
                  {_formatFileSize(uploadedFile.size)}
                </p>
              </div>
              {isProcessing && (
                <div className="flex items-center gap-2 text-information-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-information-600 border-t-transparent"></div>
                  <span className="text-xs">Processing...</span>
                </div>
              )}
            </div>
            {!isProcessing && (
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-error-600 hover:text-error-700 p-2"
                aria-label="Remove file"
              >
                <MdError className="text-xl" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* File Requirements */}
      <div className="bg-primary-50 rounded-lg p-4 border border-borderColor">
        <h3 className="text-sm font-semibold text-theme mb-2">File Requirements</h3>
        <ul className="text-xs text-textLight space-y-1 list-disc list-inside">
          <li>First row must contain column headers</li>
          <li>Required fields: First Name, Last Name, Email</li>
          <li>Date format: YYYY-MM-DD (e.g., 2024-01-15)</li>
          <li>Maximum file size: 10MB</li>
          <li>Maximum rows: 10,000</li>
        </ul>
      </div>
    </div>
  );
};

export default BulkFileUploader;

